import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

export const FILTER_POPUP_GAP = 12;
export const FILTER_POPUP_CLASS = 'nettles-filter-popup';
export const PROJECTS_FILTER_POPUP_CLASS = 'projects-filter-popup';

function isInsideFilterPopup(target) {
  if (!target?.closest) return false;
  return (
    target.closest(`.${FILTER_POPUP_CLASS}`) ||
    target.closest(`.${PROJECTS_FILTER_POPUP_CLASS}`)
  );
}

/** Layout viewport metrics; prefers visualViewport on iOS Safari / PWA. */
export function getFilterPopupViewportMetrics() {
  if (typeof window === 'undefined') {
    return { viewportW: 0, viewportH: 0, bottomReserve: 8 };
  }

  const vv = window.visualViewport;
  const viewportW = vv?.width ?? window.innerWidth ?? 0;
  const viewportH = vv?.height ?? window.innerHeight ?? 0;

  let bottomReserve = 8;
  if (typeof document !== 'undefined') {
    const chromeOffset = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--pwa-bottom-nav-chrome')
    );
    const navOffset = Number.isFinite(chromeOffset) && chromeOffset > 0
      ? chromeOffset
      : parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--pwa-bottom-nav-offset')
        );
    if (Number.isFinite(navOffset) && navOffset > 0) {
      bottomReserve += navOffset;
    }
  }

  return { viewportW, viewportH, bottomReserve };
}

/** Measure filter button; open below the full column header cell (`th`). */
export function getFilterPopupAnchorFromButton(button) {
  if (!button) return null;

  const buttonRect = button.getBoundingClientRect();
  const th = button.closest('th');
  const headerBottom = th?.getBoundingClientRect().bottom ?? buttonRect.bottom;

  // DOMRect spread is unreliable — copy fields explicitly.
  return {
    top: buttonRect.top,
    left: buttonRect.left,
    right: buttonRect.right,
    bottom: headerBottom,
    width: buttonRect.width,
    height: buttonRect.height,
    x: buttonRect.x,
    y: buttonRect.y,
  };
}

export function getFilterPopupFixedStyle(anchorRect, gap = FILTER_POPUP_GAP) {
  if (!anchorRect || typeof window === 'undefined') return { display: 'none' };

  const margin = 8;
  const { viewportW, viewportH, bottomReserve } = getFilterPopupViewportMetrics();
  const maxWidth = Math.min(320, Math.max(240, viewportW - margin * 2));

  let left = Number.isFinite(anchorRect.left) ? anchorRect.left : margin;
  const width = Math.min(
    maxWidth,
    Math.max(Number.isFinite(anchorRect.width) ? anchorRect.width : maxWidth, 240)
  );
  if (left + width > viewportW - margin) left = viewportW - margin - width;
  if (left < margin) left = margin;

  const top = (Number.isFinite(anchorRect.bottom) ? anchorRect.bottom : 0) + gap;
  const maxHeight = Math.max(160, viewportH - top - bottomReserve);

  return {
    position: 'fixed',
    zIndex: 9999,
    top,
    left,
    width,
    maxWidth: `calc(100vw - ${margin * 2}px)`,
    maxHeight,
  };
}

function bindFilterPopupViewportListeners(updateFilterPopupAnchor) {
  window.addEventListener('resize', updateFilterPopupAnchor);
  window.addEventListener('orientationchange', updateFilterPopupAnchor);

  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener('resize', updateFilterPopupAnchor);
    vv.addEventListener('scroll', updateFilterPopupAnchor);
  }

  return () => {
    window.removeEventListener('resize', updateFilterPopupAnchor);
    window.removeEventListener('orientationchange', updateFilterPopupAnchor);
    if (vv) {
      vv.removeEventListener('resize', updateFilterPopupAnchor);
      vv.removeEventListener('scroll', updateFilterPopupAnchor);
    }
  };
}

/**
 * Portal + fixed positioning + background scroll lock for table column filters.
 * Matches Projects (ManageProject) filter popup behavior.
 */
export function useFilterPopupPortal({
  activeFilterColumn,
  setActiveFilterColumn,
  tableScrollRef,
  hScrollbarRef,
} = {}) {
  const [filterPopupAnchorRect, setFilterPopupAnchorRect] = useState(null);
  const filterDropdownRef = useRef(null);
  const popupPortalTarget = typeof document !== 'undefined' ? document.body : null;

  const closeFilterPopup = useCallback(() => {
    setActiveFilterColumn?.(null);
    setFilterPopupAnchorRect(null);
  }, [setActiveFilterColumn]);

  const toggleFilterPopup = useCallback(
    (columnName, event) => {
      event?.stopPropagation?.();
      const isOpening = activeFilterColumn !== columnName;
      if (isOpening && event?.currentTarget) {
        setFilterPopupAnchorRect(getFilterPopupAnchorFromButton(event.currentTarget));
      } else {
        setFilterPopupAnchorRect(null);
      }
      setActiveFilterColumn?.(isOpening ? columnName : null);
    },
    [activeFilterColumn, setActiveFilterColumn]
  );

  const updateFilterPopupAnchor = useCallback(() => {
    const button = filterDropdownRef.current?.querySelector('button');
    const anchor = getFilterPopupAnchorFromButton(button);
    if (anchor) {
      setFilterPopupAnchorRect(anchor);
    }
  }, []);

  useLayoutEffect(() => {
    if (!activeFilterColumn) return undefined;

    updateFilterPopupAnchor();
    return bindFilterPopupViewportListeners(updateFilterPopupAnchor);
  }, [activeFilterColumn, updateFilterPopupAnchor]);

  useEffect(() => {
    if (!activeFilterColumn) return undefined;

    const scrollEl = tableScrollRef?.current;
    const hScrollEl = hScrollbarRef?.current;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousScrollOverflow = scrollEl?.style.overflow ?? '';
    const previousScrollTouchAction = scrollEl?.style.touchAction ?? '';
    const previousHScrollPointerEvents = hScrollEl?.style.pointerEvents ?? '';

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    if (scrollEl) {
      scrollEl.style.overflow = 'hidden';
      scrollEl.style.touchAction = 'none';
    }

    if (hScrollEl) {
      hScrollEl.style.pointerEvents = 'none';
    }

    updateFilterPopupAnchor();
    const rafId = requestAnimationFrame(updateFilterPopupAnchor);

    const blockBackgroundScroll = (event) => {
      if (isInsideFilterPopup(event.target)) return;
      event.preventDefault();
    };

    document.addEventListener('touchmove', blockBackgroundScroll, { passive: false });
    document.addEventListener('wheel', blockBackgroundScroll, { passive: false });

    return () => {
      cancelAnimationFrame(rafId);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;

      if (scrollEl) {
        scrollEl.style.overflow = previousScrollOverflow;
        scrollEl.style.touchAction = previousScrollTouchAction;
      }

      if (hScrollEl) {
        hScrollEl.style.pointerEvents = previousHScrollPointerEvents;
      }

      document.removeEventListener('touchmove', blockBackgroundScroll);
      document.removeEventListener('wheel', blockBackgroundScroll);
    };
  }, [activeFilterColumn, tableScrollRef, hScrollbarRef, updateFilterPopupAnchor]);

  const renderFilterPopup = useCallback(
    (content) => {
      if (popupPortalTarget && filterPopupAnchorRect) {
        return ReactDOM.createPortal(
          <div style={getFilterPopupFixedStyle(filterPopupAnchorRect)}>{content}</div>,
          popupPortalTarget
        );
      }

      return (
        <div className="absolute top-full left-0 z-[60] mt-3 w-full min-w-[14rem] max-w-[20rem]">
          {content}
        </div>
      );
    },
    [filterPopupAnchorRect, popupPortalTarget]
  );

  const getFilterWrapperStyle = useCallback(
    (isActive) => ({
      overflow: 'visible',
      zIndex: isActive ? 60 : 1,
    }),
    []
  );

  return {
    filterDropdownRef,
    filterPopupAnchorRect,
    toggleFilterPopup,
    closeFilterPopup,
    renderFilterPopup,
    getFilterWrapperStyle,
  };
}
